'use client';

import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import { transactionsApi } from '@/lib/api';
import type {
  RefundListFilter,
  TransactionsMetaResponse,
  TransactionsQueryOptions,
  TransactionsResponse,
} from '@/lib/api';
import { useOnReturnToTab, useOnSpaceSendingChanged } from '@/lib/spaces/sendingChanges';
import type { RecurrenceFilter } from '@/lib/transactions/types';
import { TRANSACTION_SWR_OPTIONS, useAuthedFetcher } from './swrConfig';

// The API's largest transactions page: it refuses a larger `limit` (400) instead of clamping it.
export const TRANSACTIONS_MAX_PAGE = 200;

export interface TransactionsPageParams {
  from?: Date;
  to?: Date;
  accountId?: string;
  type?: string;
  paymentMethod?: string;
  isInstallment?: boolean;
  isExpense?: boolean;
  isBankIncome?: boolean;
  recurringGroup?: readonly RecurrenceFilter[];
  categoryId?: string | string[];
  parentCategoryId?: string | string[];
  refund?: RefundListFilter;
  platform?: string;
  /** Tag membership (VER-378), OR-unioned by the API. */
  tagIds?: string[];
  q?: string;
  sortField?: 'date' | 'value' | 'merchant';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

function listKeyPart(value?: string | string[] | null) {
  return Array.isArray(value) ? value.join(',') : value ?? null;
}

// Map the screen's page params onto the API query shape. Shared by both list hooks (paged + infinite)
// so the request built from a given filter set is identical regardless of which one issued it.
function toQueryOptions(params: TransactionsPageParams, offset?: number): TransactionsQueryOptions {
  return {
    from: params.from?.toISOString(),
    to: params.to?.toISOString(),
    accountId: params.accountId,
    type: params.type,
    paymentMethod: params.paymentMethod,
    isInstallment: params.isInstallment,
    isExpense: params.isExpense,
    isBankIncome: params.isBankIncome,
    recurringGroup: params.recurringGroup,
    categoryId: params.categoryId,
    parentCategoryId: params.parentCategoryId,
    refund: params.refund,
    platform: params.platform,
    tagId: params.tagIds,
    q: params.q,
    sortField: params.sortField,
    sortOrder: params.sortOrder,
    limit: params.limit,
    offset: offset ?? params.offset,
  };
}

function buildKey(prefix: string, params: TransactionsPageParams, offset?: number) {
  return [
    prefix,
    params.from?.toISOString() ?? null,
    params.to?.toISOString() ?? null,
    params.accountId ?? null,
    params.type ?? null,
    params.paymentMethod ?? null,
    params.isInstallment ?? null,
    params.isExpense ?? null,
    params.isBankIncome ?? null,
    params.recurringGroup?.join(',') ?? null,
    listKeyPart(params.categoryId),
    listKeyPart(params.parentCategoryId),
    params.refund ?? null,
    params.platform ?? null,
    // Inserted BEFORE q/sort/offset: the infinite fetcher reads the offset as the
    // key's LAST element, so new parts must never trail it.
    listKeyPart(params.tagIds ?? null),
    params.q ?? null,
    params.sortField ?? 'date',
    params.sortOrder ?? 'desc',
    params.limit ?? null,
    offset ?? params.offset ?? 0,
  ] as const;
}

export function useTransactionsPage(params: TransactionsPageParams | null) {
  const authedFetch = useAuthedFetcher();

  const key = params ? buildKey('transactions', params) : null;

  return useSWR<TransactionsResponse>(
    key,
    async () => {
      if (!params) throw new Error('Missing params');
      return authedFetch((token) => transactionsApi.getAll(token, toQueryOptions(params)));
    },
    TRANSACTION_SWR_OPTIONS,
  );
}

export function useTransactionsInfinite(params: TransactionsPageParams | null) {
  const authedFetch = useAuthedFetcher();

  const pages = useSWRInfinite<TransactionsResponse>(
    (pageIndex, previousPageData) => {
      if (!params) return null;
      if (previousPageData && previousPageData.hasMore === false) return null;
      const pageSize = params.limit ?? 50;
      const nextOffset =
        pageIndex === 0
          ? params.offset ?? 0
          : previousPageData?.nextOffset ?? pageIndex * pageSize;
      return buildKey('transactions-infinite', params, nextOffset);
    },
    async (key) => {
      if (!params) throw new Error('Missing params');
      const offset = Number(key[key.length - 1] ?? 0);
      return authedFetch((token) =>
        transactionsApi.getAll(token, toQueryOptions(params, offset)),
      );
    },
    {
      ...TRANSACTION_SWR_OPTIONS,
      keepPreviousData: false,
      revalidateFirstPage: false,
    },
  );
  // A space write changes the rows' markers (VER-571), and a filter mutate never reaches a mounted infinite list:
  // its pages are read again here, and when the person comes back to the tab, since a write in another tab sends no
  // signal and SWR's own focus revalidation doesn't read the loaded pages again (no `revalidateAll`).
  const { mutate } = pages;
  const readPagesAgain = () => {
    if (params) void mutate();
  };
  useOnSpaceSendingChanged(readPagesAgain);
  useOnReturnToTab(readPagesAgain);
  return pages;
}

export function useTransactionsMeta(params: TransactionsPageParams | null) {
  const authedFetch = useAuthedFetcher();
  const key = params ? buildKey('transactions-meta', params, 0) : null;

  return useSWR<TransactionsMetaResponse>(
    key,
    async () => {
      if (!params) throw new Error('Missing params');
      return authedFetch((token) =>
        transactionsApi.getMeta(token, {
          from: params.from?.toISOString(),
          to: params.to?.toISOString(),
          accountId: params.accountId,
          type: params.type,
          paymentMethod: params.paymentMethod,
          isInstallment: params.isInstallment,
          isExpense: params.isExpense,
          isBankIncome: params.isBankIncome,
          recurringGroup: params.recurringGroup,
          categoryId: params.categoryId,
          parentCategoryId: params.parentCategoryId,
          refund: params.refund,
          platform: params.platform,
          tagId: params.tagIds,
          q: params.q,
        }),
      );
    },
    TRANSACTION_SWR_OPTIONS,
  );
}
